import { useEffect, useId, useRef, useState } from 'react';

import { initials } from 'components/person-avatar';
import { FOCO_VISIVEL } from 'utils/foco';

import {
  dataDoCadastro,
  faixaDaEquipe,
  indicadorDaRealizacao,
  periodoDaEntrega,
  RealizacaoParaAutorizar,
  statusDaAutorizacao,
  tamanhoDaEquipe,
  textoDaEquipe,
} from './autorizacao-regras';

export interface CartaoDeAutorizacaoProps {
  realizacao: RealizacaoParaAutorizar;
  /** Chave de quem está logado: decide o "por você" do estado resolvido. */
  usuario: string | undefined;
  /** `canApproveParticipantsCount` do gestor efetivo (PAR-07, PAR-08). */
  dentroDaAlcada: boolean;
  prazoEncerrado: boolean;
  /** `podeDesfazer` (PAR-37). */
  desfazivel: boolean;
  gravando: boolean;
  /** Recusa ou falha da última gravação deste cartão (PAR-24, PAR-39). */
  erro: string | null;
  onAutorizar: () => void;
  onRecusar: () => void;
  onDesfazer: () => void;
}

// Rótulos de botão em caixa alta, como o `Button` da nova identidade.
const ROTULO_DE_BOTAO = 'font-bold uppercase tracking-[0.04em] text-jj-label';

function textoResolvido(aprovada: boolean, porVoce: boolean): string {
  const texto = aprovada ? 'Quantidade autorizada' : 'Quantidade não autorizada';
  return porVoce ? `${texto} por você.` : `${texto}.`;
}

function Marca({ aprovada }: { aprovada: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-brand-white ${
        aprovada ? 'bg-brand-blue' : 'bg-brand-pink'
      }`}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" focusable="false">
        <path d={aprovada ? 'm5 13 4 4 10-10' : 'M18 6 6 18M6 6l12 12'} />
      </svg>
    </span>
  );
}

/** Uma realização coletiva com a faixa, a alçada exigida e a decisão do líder (design `Minha Equipe.dc.html`, visão participantes). */
export default function CartaoDeAutorizacao({
  realizacao,
  usuario,
  dentroDaAlcada,
  prazoEncerrado,
  desfazivel,
  gravando,
  erro,
  onAutorizar,
  onRecusar,
  onDesfazer,
}: CartaoDeAutorizacaoProps) {
  const idDoTitulo = useId();
  const idDoDetalhe = useId();
  const [detalheAberto, setDetalheAberto] = useState(false);

  const cartao = useRef<HTMLElement>(null);
  const autorizar = useRef<HTMLButtonElement>(null);
  const desfazer = useRef<HTMLButtonElement>(null);

  const status = statusDaAutorizacao(realizacao);
  const pendente = status === 'pending';
  const aprovada = status === 'approved';
  const participantes = tamanhoDaEquipe(realizacao);
  const faixa = faixaDaEquipe(participantes);
  const porVoce = !!usuario && realizacao.participants_approval?.decided_by === usuario;
  const titulo = realizacao.title ?? '';

  // Depois de decidir, o foco vai para "Desfazer" (PAR-23); depois de desfazer, para "Autorizar quantidade" (PAR-38).
  // Os botões de origem saem da tela, e o foco cairia no `body`.
  const statusAnterior = useRef(status);
  useEffect(() => {
    if (statusAnterior.current === status) return;
    const decidiuAgora = statusAnterior.current === 'pending';
    statusAnterior.current = status;

    const alvo = decidiuAgora ? desfazer.current : autorizar.current;
    if (alvo && !alvo.disabled) alvo.focus();
    else cartao.current?.focus();
  }, [status]);

  const borda = pendente ? 'border-[#C8CEDA]' : aprovada ? 'border-brand-green' : 'border-brand-pink-light';

  return (
    <article
      ref={cartao}
      tabIndex={-1}
      aria-labelledby={idDoTitulo}
      aria-busy={gravando}
      // D3: borda cinza #C8CEDA do design sem token; 16/18px de padding sem degrau.
      className={`flex flex-col gap-3 rounded-jj-lg border border-solid px-[18px] py-4 shadow-[0_1px_3px_rgba(17,24,39,.06)] outline-none ${borda} ${
        pendente ? 'bg-brand-white' : 'bg-[#FCFDFE]'
      }`}
    >
      <div className="flex flex-wrap items-start gap-[13px]">
        <span
          aria-hidden="true"
          className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-brand-green font-display text-jj-body-sm font-bold text-brand-navy"
        >
          {initials(realizacao.owner_name)}
        </span>
        <div className="min-w-[200px] flex-1">
          <h3 id={idDoTitulo} className="m-0 text-jj-body-sm font-bold leading-jj-caption text-content">
            {titulo}
          </h3>
          <p className="m-0 mt-[5px] text-jj-label text-content-soft">
            Cadastrada por {realizacao.owner_name} ({realizacao.owner}) · {dataDoCadastro(realizacao.created)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <span className="sr-only">{participantes} participantes</span>
          <div aria-hidden="true">
            <div className="text-jj-micro uppercase tracking-[0.05em] text-content-soft">Participantes</div>
            {/* D3: 24px sem degrau na escala (22 e 27). */}
            <div className="font-display text-[24px] font-extrabold leading-[1.1] text-brand-blue">{participantes}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 rounded-jj-md bg-surface-subtle px-3.5 py-2.5">
        {faixa && (
          <>
            <span className="text-jj-label text-content-soft">Faixa {faixa.rotulo} · alçada exigida</span>
            <span className="text-jj-label font-bold text-brand-blue-dark">{faixa.alcada}</span>
          </>
        )}
        {pendente && (
          // SPEC_DEVIATION: "acima da alçada" em `text-urgent` (o `brand-pink` do design dá 2,6:1 no cinza).
          <span className={`ml-auto text-jj-label font-bold ${dentroDaAlcada ? 'text-brand-blue' : 'text-urgent'}`}>
            {dentroDaAlcada ? 'Dentro da sua alçada' : 'Acima da sua alçada · encaminhado'}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-0 border-t border-solid border-[#F1F2F5] pt-3">
        {pendente && prazoEncerrado && <p className="m-0 text-jj-caption text-content">O prazo de autorização terminou.</p>}

        {pendente && !prazoEncerrado && (
          <>
            {/* SPEC_DEVIATION: fundo `brand-blue-dark` (amarelo de 11px sobre `brand-blue` dá 4,44:1, PAR-43). */}
            <button
              ref={autorizar}
              type="button"
              onClick={onAutorizar}
              disabled={!dentroDaAlcada || gravando}
              aria-label={`Autorizar quantidade de ${titulo}`}
              className={`cursor-pointer rounded-jj-field border-0 bg-brand-blue-dark px-[22px] py-[11px] text-brand-yellow disabled:cursor-not-allowed disabled:opacity-[.45] ${ROTULO_DE_BOTAO} ${FOCO_VISIVEL}`}
            >
              Autorizar quantidade
            </button>
            {/* SPEC_DEVIATION: texto em `text-urgent` com a borda rosa do design (`brand-pink` no branco dá 2,6:1). */}
            <button
              type="button"
              onClick={onRecusar}
              disabled={!dentroDaAlcada || gravando}
              aria-label={`Não autorizar ${titulo}`}
              className={`cursor-pointer rounded-jj-field border border-solid border-brand-pink bg-brand-white px-5 py-[11px] text-urgent hover:bg-[#FFF1F5] disabled:cursor-not-allowed disabled:opacity-[.45] ${ROTULO_DE_BOTAO} ${FOCO_VISIVEL}`}
            >
              Não autorizar
            </button>
          </>
        )}

        {!pendente && status && (
          <>
            <span className="inline-flex items-center gap-2 text-jj-caption text-content">
              <Marca aprovada={aprovada} />
              {textoResolvido(aprovada, porVoce)}
            </span>
            {desfazivel && (
              <button
                ref={desfazer}
                type="button"
                onClick={onDesfazer}
                disabled={gravando}
                aria-label={`Desfazer decisão de ${titulo}`}
                className={`cursor-pointer border-0 bg-transparent p-0 text-brand-blue disabled:cursor-not-allowed disabled:opacity-[.45] ${ROTULO_DE_BOTAO} ${FOCO_VISIVEL}`}
              >
                Desfazer
              </button>
            )}
          </>
        )}

        <button
          type="button"
          onClick={() => setDetalheAberto(aberto => !aberto)}
          aria-expanded={detalheAberto}
          aria-controls={idDoDetalhe}
          className={`ml-auto inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-content-soft ${ROTULO_DE_BOTAO} ${FOCO_VISIVEL}`}
        >
          <span aria-hidden="true" className="text-jj-body-sm leading-none">
            {detalheAberto ? '−' : '+'}
          </span>
          {detalheAberto ? 'Ocultar informações' : 'Ver mais informações'}
        </button>
      </div>

      {erro && (
        <p role="alert" className="m-0 text-jj-caption font-bold text-urgent">
          {erro}
        </p>
      )}

      <div id={idDoDetalhe} hidden={!detalheAberto}>
        {detalheAberto && (
          <dl className="m-0 grid grid-cols-1 gap-3 rounded-jj-lg bg-surface-subtle px-4 py-3.5 sm:grid-cols-2">
            <div>
              <dt className="mb-1 text-jj-micro text-content-soft">Indicador</dt>
              <dd className="m-0 text-jj-label leading-jj-body-sm">{indicadorDaRealizacao(realizacao)}</dd>
            </div>
            <div>
              <dt className="mb-1 text-jj-micro text-content-soft">Período da entrega</dt>
              <dd className="m-0 text-jj-label leading-jj-body-sm">{periodoDaEntrega(realizacao)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="mb-1 text-jj-micro text-content-soft">Equipe</dt>
              <dd className="m-0 text-jj-label leading-jj-body">{textoDaEquipe(realizacao)}</dd>
            </div>
          </dl>
        )}
      </div>
    </article>
  );
}
